import axios, { AxiosRequestConfig } from 'axios';
import vm from '@/main';
import users from '@/shared/services/users';
import appConfig from '@/config';

/**
 * Waits until a token refresh request is completed or a maximum timeout is reached.
 * Prevents multiple concurrent token refresh calls.
 */
const waitForTokenRequestToComplete = (): Promise<void> => new Promise((resolve) => {
  const app = vm();
  let timeElapsed = 0;
  const maxWait = 3000;
  const interval = setInterval(() => {
    timeElapsed += 200;
    if (!app?.$store.getters.isTokenRequestInProgress || timeElapsed >= maxWait) {
      clearInterval(interval);
      resolve();
    } else {
      console.warn('--> Waiting for re-auth');
    }
  }, 200);
});

/**
 * Determines if a URL points to a login or token endpoint.
 */
export const isLoginRoute = (url?: string): boolean => !!(
  url?.startsWith(`${appConfig?.oauthService}/public/user/login`)
  || url?.startsWith(`${appConfig?.oauthService}/auth/token/refresh`)
  || url?.startsWith(`${appConfig?.oauthService}/public/bstk/sso`)
  || url?.startsWith(`${appConfig?.oauthService.replace('.rentsync.dev', '.rentsync.io')}/public/user/login`)
  || url?.startsWith(`${appConfig?.oauthService.replace('.rentsync.dev', '.rentsync.io')}/auth/token/refresh`)
  || url?.startsWith(`${appConfig?.oauthService.replace('.rentsync.dev', '.rentsync.io')}/public/bstk/sso`)
);

/**
 * Handles token refresh logic for 401 responses.
 * - Refreshes the token if needed.
 * - Retries the original request after refresh.
 * - Redirects to login if refresh fails.
 */
export const handleTokenRefresh = async (config?: AxiosRequestConfig, status: number = 0, app?: Vue): Promise<any | null> => {
  if ([401].includes(status) && !isLoginRoute(config?.url)) {
    try {
      if (app?.$env?.VUE_APP_USE_AXIOS_REQUEST_INTERVAL === '1') {
        if (!app?.$store.getters.isTokenRequestInProgress) {
          app?.$store.commit('setIsTokenRequestInProgress', true);
          await users.token();
          app?.$store.commit('setIsTokenRequestInProgress', false);
        } else {
          await waitForTokenRequestToComplete();
        }
      } else {
        await users.token();
      }

      if (config) {
        const res = await axios(config);
        return res.data;
      }
    } catch (err) {
      if (app) {
        app.$store.commit('users/setCurrent', null);
        app.$router.push({
          path: '/login',
          query: {
            ...!isLoginRoute(config?.url) && { redirect: app.$route.fullPath },
          },
        });
      }

      return null;
    }
  }

  return null;
};
